import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  TablePagination,
  TableSortLabel,
  Box,
} from "@mui/material";
import { StyledTableRow } from "../../../styles/Shipmentstyles";
import { useNavigate } from "react-router-dom";
import { InventorySwagpack } from "../../../models/inventory/InventorySwagpack";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import { SortType } from "../../../models/SortType";
import CustomTypography from "../common/form/CustomTypography";

interface InventorySwagpackTableProps {
  maxHeight: number;
  swagpacks: InventorySwagpack[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  minHeight?: number;
}

const InventorySwagpackTable = ({
  maxHeight,
  swagpacks,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  minHeight = window.innerHeight - 350,
}: InventorySwagpackTableProps) => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ width: "100%" }}>
              <AppTableHeaderCell  sx={{ width: 110, minWidth: 110, textAlign: "center" }} >
                <TableSortLabel
                  active={sortBy === "id"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "id",
                      sortBy === "id" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => <span>{renderSortIcon("id")}</span>}
                  sx={{ fontSize: "14px" }}
                >
                  PACK ID
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell title="LABEL" fontSize="14px" />
              <AppTableHeaderCell sx={{maxWidth: 130}}  title="QUANTITY" fontSize="14px" />
              <AppTableHeaderCell sx={{maxWidth: 160}} title="USED QUANTITY" fontSize="14px" />
              <AppTableHeaderCell sx={{maxWidth: 170}} title="BLOCKED QUANTITY" fontSize="14px" />
              <AppTableHeaderCell  sx={{maxWidth: 180}} title="AVAILABLE QUANTITY" fontSize="14px" />
              <AppTableHeaderCell sx={{maxWidth: 140}} title="PRODUCT COUNT" fontSize="14px" />
              <AppTableHeaderCell title="CREATED BY" fontSize="14px" />
              <AppTableHeaderCell sx={{maxWidth: 130}} title="ACTIVE" fontSize="14px" />

              <AppTableHeaderCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={sortBy === "created_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "created_date",
                      sortBy === "created_date" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("created_date")}</span>
                  )}
                  sx={{ fontSize: "14px" }}
                >
                  CREATED DATE
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={sortBy === "modified_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "modified_date",
                      sortBy === "modified_date" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("modified_date")}</span>
                  )}
                  sx={{ fontSize: "14px" }}
                >
                  MODIFIED DATE
                </TableSortLabel>
              </AppTableHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {swagpacks.map((swagpack) => (
              <StyledTableRow key={swagpack.id}>
                <TableCell
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    textAlign: "center",
                    fontSize: 14,
                  }}
                >
                  <Box
                    onClick={() =>
                      navigate(`/inventory/packs/${swagpack.id}/variants`)
                    }
                  >
                    {swagpack.id}
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {swagpack.label || "-"}
                </TableCell>

                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {(swagpack.quantity ?? 0) + (swagpack.blocked_quantity ?? 0)}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {swagpack.used_quantity ?? "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {swagpack.blocked_quantity}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {swagpack.quantity !== undefined
                    ? swagpack.quantity -
                      ((swagpack.blocked_quantity ?? 0) +
                        (swagpack.used_quantity ?? 0))
                    : "-"}
                </TableCell>

                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {swagpack.products_count ?? "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {swagpack.created_by_name || "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {swagpack.active ? "Yes" : "No"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {new Date(swagpack.created_date).toLocaleString()}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {new Date(swagpack.modified_date).toLocaleString()}
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        {swagpacks.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 15,
            }}
          >
            <CustomTypography
              color={theme.palette.customColors.greyTextColor}
              variant="body2"
              fontWeight={700}
            >
              NO SWAGPACK FOUND!
            </CustomTypography>
          </Box>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[30, 50, 100, 150, 200]}
        component="div"
        count={total_count}
        rowsPerPage={limit}
        page={page_no - 1}
        onPageChange={(event, page_no) => onPageChange(event, page_no + 1)}
        onRowsPerPageChange={(event) =>
          handleRowsChange(parseInt(event.target.value, 10))
        }
      />
    </>
  );
};

export default InventorySwagpackTable;
